import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import './App.css';

/* Public pages */
const LandingPage = lazy(() => import('./pages/LandingPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const Login = lazy(() => import('./pages/Login'));

/* App pages */
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Customers = lazy(() => import('./pages/Customers'));
const CustomerView = lazy(() => import('./pages/CustomerView'));
const Quotations = lazy(() => import('./pages/Quotations'));
const QuotationForm = lazy(() => import('./pages/QuotationForm'));
const QuotationPreview = lazy(() => import('./pages/QuotationPreview'));
const Templates = lazy(() => import('./pages/Templates'));
const TemplateForm = lazy(() => import('./pages/TemplateForm'));
const Leads = lazy(() => import('./pages/Leads'));
const LeadView = lazy(() => import('./pages/LeadView'));
const Agreements = lazy(() => import('./pages/Agreements'));
const AgreementForm = lazy(() => import('./pages/AgreementForm'));
const Invoices = lazy(() => import('./pages/Invoices'));
const InvoiceCreate = lazy(() => import('./pages/InvoiceCreate'));
const InvoicePreview = lazy(() => import('./pages/InvoicePreview'));
const TermsConditions = lazy(() => import('./pages/TermsConditions'));
const TermsConditionsForm = lazy(() => import('./pages/TermsConditionsForm'));
const TermsConditionsView = lazy(() => import('./pages/TermsConditionsView'));
const Reports = lazy(() => import('./pages/Reports'));
const Settings = lazy(() => import('./pages/Settings'));
const EnquiryForm = lazy(() => import('./pages/EnquiryForm'));
const Enquiries = lazy(() => import('./pages/Enquiries'));

/* Scroll to top on every route change */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3500,
          success: {
            style: {
              background: '#fff',
              color: '#15803d',
              border: '1px solid rgba(22, 163, 74, 0.3)',
            },
            iconTheme: { primary: '#16a34a', secondary: '#fff' },
          },
          error: {
            style: {
              background: '#fff',
              color: '#b91c1c',
              border: '1px solid rgba(220, 38, 38, 0.3)',
            },
            iconTheme: { primary: '#dc2626', secondary: '#fff' },
          },
          style: {
            background: '#fff',
            color: '#a16207',
            border: '1px solid rgba(234, 179, 8, 0.3)',
          },
        }}
      />
      <Router>
        <ScrollToTop />
        <Suspense fallback={null}>
        <Routes>
          {/* ── Public website routes ── */}
          <Route path="/"         element={<LandingPage />} />
          <Route path="/about"    element={<AboutPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/blog"     element={<BlogPage />} />
          <Route path="/contact"  element={<ContactPage />} />
          <Route path="/login"    element={<Login />} />
          <Route path="/enquire"  element={<EnquiryForm />} />

          {/* ── Protected app routes ── */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard"              element={<Dashboard />} />
            <Route path="customers"              element={<Customers />} />
            <Route path="customers/:id"          element={<CustomerView />} />
            <Route path="quotations"             element={<Quotations />} />
            <Route path="quotations/new"         element={<QuotationForm />} />
            <Route path="quotations/:id"         element={<QuotationForm />} />
            <Route path="quotations/:id/preview" element={<QuotationPreview />} />
            <Route path="templates"              element={<Templates />} />
            <Route path="templates/new"          element={<TemplateForm />} />
            <Route path="templates/:id"          element={<TemplateForm />} />
            <Route path="leads"                  element={<Leads />} />
            <Route path="leads/:id"              element={<LeadView />} />
            <Route path="agreements"             element={<Agreements />} />
            <Route path="agreements/new"         element={<AgreementForm />} />
            <Route path="agreements/:id"         element={<AgreementForm />} />
            <Route path="invoices"               element={<Invoices />} />
            <Route path="invoices/new"           element={<InvoiceCreate />} />
            <Route path="invoices/:id"           element={<InvoiceCreate />} />
            <Route path="invoices/preview"       element={<InvoicePreview />} />
            <Route path="invoices/:id/preview"   element={<InvoicePreview />} />
            <Route path="terms"                  element={<TermsConditions />} />
            <Route path="terms/new"              element={<TermsConditionsForm />} />
            <Route path="terms/:id"              element={<TermsConditionsView />} />
            <Route path="terms/:id/edit"         element={<TermsConditionsForm />} />
            <Route path="enquiries"              element={<Enquiries />} />
            <Route path="reports"                element={<Reports />} />
            <Route path="settings"               element={<Settings />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
